import React, { useEffect, useRef, useState } from "react";
import { AsYouType, parsePhoneNumberFromString, getCountryCallingCode } from "libphonenumber-js";
import CountrySelect from "components/CountrySelect";
import { __ } from "services/Translator";

export default function PhoneInput({
  id,
  ariaDescribedBy,
  defaultCountry = "US",
  value = "",
  onChange,
  placeholder,
  inputClassName = "name_value",
  containerClassName = "phone_input_container",
  invalidClassName = "error",
  autoDetect = "initial",
  unlockOnClear = true,
  onFocus,
}) {
  const initialDefaultRef = useRef((defaultCountry || "US").toUpperCase());

  const ensureCountryObj = (iso2) => {
    if (!iso2) return null;
    const code = iso2.toUpperCase();
    let phonecode = "";
    try {
      phonecode = String(getCountryCallingCode(code));
    } catch (_) {
      // ignore
    }
    return { code, phonecode };
  };

  const [country, setCountry] = useState(() => ensureCountryObj(initialDefaultRef.current));
  const [uiValue, setUiValue] = useState("");
  const [e164, setE164] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [countryLocked, setCountryLocked] = useState(false);

  const didInitialAutoDetect = useRef(false);

  const normalize = (raw) => {
    if (!raw) return "";
    let v = raw.trim();
    if (v.startsWith("00")) v = "+" + v.slice(2);
    return v.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
  };

  const currentIso = () => country?.code || initialDefaultRef.current;

  const prettyFormatNational = (national, iso2) => {
    try {
      return new AsYouType((iso2 || "").toUpperCase()).input(national);
    } catch (_) {
      return national;
    }
  };

  const computeFromParts = (national, iso2) => {
    const digits = (national || "").replace(/[^\d]/g, "");
    let e = "";
    let valid = false;
    let ui = national || "";
    try {
      const calling = getCountryCallingCode((iso2 || "").toUpperCase());
      const full = "+" + calling + digits;
      let pn = parsePhoneNumberFromString(full);
      // A pasted "+61 4..." or "0061 4..." leaves the calling code inside the
      // national part; prefixing it again would produce +6161... Only fall back
      // to reading the digits as a full number when the naive reading is invalid.
      if ((!pn || !pn.isValid()) && digits.startsWith(calling)) {
        const asFull = parsePhoneNumberFromString("+" + digits);
        if (asFull && asFull.isValid()) pn = asFull;
      }
      if (pn) {
        valid = pn.isValid();
        e = pn.number;
        ui = pn.formatNational();
      }
    } catch (_) {
      // ignore
    }
    return { e164: e, isValid: valid, ui };
  };

  useEffect(() => {
    const nextIso = (defaultCountry || "US").toUpperCase();
    if (!nextIso || countryLocked || uiValue) {
      return;
    }
    setCountry(ensureCountryObj(nextIso));
  }, [defaultCountry, countryLocked, uiValue]);

  useEffect(() => {
    if (!value) {
      setUiValue("");
      setE164("");
      setIsValid(true);
      if (unlockOnClear) setCountryLocked(false);
      return;
    }

    if (autoDetect === "never") return;
    if (autoDetect === "initial" && didInitialAutoDetect.current) return;
    if (countryLocked && autoDetect !== "always") return;

    const norm = normalize(value);
    const pn = parsePhoneNumberFromString(norm);
    if (pn) {
      const rec = ensureCountryObj(pn.country || currentIso());
      if (!countryLocked || autoDetect === "always") {
        setCountry(rec);
      }
      setUiValue(pn.formatNational());
      setE164(pn.number);
      setIsValid(pn.isValid());
      onChange?.({ ui: pn.formatNational(), e164: pn.number, isValid: pn.isValid(), country: rec });
      if (autoDetect === "initial") didInitialAutoDetect.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (ui, e, valid, ctry = country) => {
    onChange?.({ ui, e164: e, isValid: valid, country: ctry });
  };

  const onNumberChange = (ev) => {
    const raw = ev.target.value;
    const norm = normalize(raw);

    if (!norm) {
      setUiValue("");
      setE164("");
      setIsValid(true);
      if (unlockOnClear) setCountryLocked(false);
      emit("", "", true);
      return;
    }

    const allowAutoOnChange = autoDetect === "always" || (autoDetect === "initial" && !countryLocked);

    if (norm.startsWith("+") && allowAutoOnChange) {
      const pn = parsePhoneNumberFromString(norm);
      if (pn && pn.country) {
        const rec = ensureCountryObj(pn.country);
        setCountry(rec);
        setUiValue(pn.formatNational());
        setE164(pn.number);
        setIsValid(pn.isValid());
        emit(pn.formatNational(), pn.number, pn.isValid(), rec);
        return;
      }
      setUiValue(norm);
      setE164(norm);
      setIsValid(false);
      emit(norm, norm, false);
      return;
    }

    const iso2 = currentIso();
    const uiPretty = prettyFormatNational(norm.replace(/^\+/, ""), iso2);
    const res = computeFromParts(uiPretty, iso2);

    setUiValue(uiPretty);
    setE164(res.e164);
    setIsValid(res.isValid);
    emit(uiPretty, res.e164, res.isValid);
  };

  const onCountryChange = (rec) => {
    const minimal = rec
      ? { code: (rec.code || "").toUpperCase(), phonecode: String(rec.phonecode || "") }
      : null;
    setCountry(minimal);
    setCountryLocked(true);
    const iso2 = minimal?.code || currentIso();
    const res = computeFromParts(uiValue, iso2);
    setUiValue(res.ui);
    setE164(res.e164);
    setIsValid(res.isValid);
    emit(res.ui, res.e164, res.isValid, minimal);
  };

  const inputClass = `${inputClassName} ${isValid ? "" : invalidClassName}`.trim();

  return (
    <div className={containerClassName} style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <CountrySelect
        selected={country}
        onChange={onCountryChange}
        placeholder={__("Search country or code...")}
        variant="phone"
        position="below"
      />
      <input
        id={id}
        aria-describedby={ariaDescribedBy}
        type="tel"
        className={inputClass}
        value={uiValue}
        onChange={onNumberChange}
        onFocus={onFocus}
        placeholder={placeholder}
        inputMode="tel"
        autoComplete="tel"
        style={{ flex: 1 }}
      />
    </div>
  );
}
